import React, { useState } from 'react';
import { HelpCircle, BookOpen, Search, Sparkles, Settings, Languages, MessageSquare, Download, Upload, RotateCcw, ArrowRight, ChevronLeft, ChevronRight, Edit3, Plus, Copy, Keyboard, Bookmark, LayoutList, Library, Cloud } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const Section = ({ icon: Icon, title, iconColor = '#d4af37', children }) => (
    <div className="p-4 bg-[#0f172a]/50 rounded-lg border border-[#1e293b] space-y-3 overflow-hidden">
        <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 flex-shrink-0`} style={{ color: iconColor }} />
            <h3 className="text-[#f8fafc] font-semibold text-sm">{title}</h3>
        </div>
        <div className="text-[#94a3b8] text-sm leading-relaxed space-y-2" style={{ overflowWrap: 'anywhere' }}>
            {children}
        </div>
    </div>
);

const Tip = ({ children }) => (
    <div className="flex items-start gap-2 p-2 bg-[#d4af37]/5 rounded border border-[#d4af37]/20">
        <span className="text-[#d4af37] text-xs font-bold mt-0.5">TIP</span>
        <p className="text-xs text-[#d4af37]/80">{children}</p>
    </div>
);

const Step = ({ number, children }) => (
    <div className="flex items-start gap-2">
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#d4af37]/20 text-[#d4af37] text-xs flex items-center justify-center font-bold">
            {number}
        </span>
        <p className="text-sm text-[#94a3b8]">{children}</p>
    </div>
);

const HelpGuide = () => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#facc15] gap-2"
                    data-testid="help-guide-btn"
                >
                    <HelpCircle className="w-4 h-4" />
                    <span className="hidden sm:inline">Help</span>
                </Button>
            </DialogTrigger>

            <DialogContent className="bg-[#0f172a] border-[#1e293b] text-[#f8fafc] max-w-2xl max-h-[85vh] overflow-hidden p-0 rounded-xl" style={{ width: 'min(92vw, 42rem)', boxSizing: 'border-box' }}>
                <DialogHeader className="p-4 sm:p-6 pb-0">
                    <DialogTitle className="flex items-center gap-2 text-[#f8fafc] heading-primary text-lg sm:text-xl">
                        <HelpCircle className="w-5 h-5 text-[#d4af37]" />
                        How to Use Ai Quran
                    </DialogTitle>
                    <p className="text-xs sm:text-sm text-[#94a3b8] mt-1">
                        Your AI-powered tool for studying the Quran word by word
                    </p>
                </DialogHeader>

                <Tabs defaultValue="getting-started" className="flex flex-col min-h-0 min-w-0 w-full">
                    <div className="px-4 sm:px-6 pt-3 sm:pt-4 min-w-0">
                        <div className="overflow-x-auto -mx-1 px-1 pb-1" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                            <TabsList className="flex w-max min-w-full bg-[#09090b] border border-[#1e293b] p-1 h-auto">
                                <TabsTrigger
                                    value="getting-started"
                                    className="flex-1 whitespace-nowrap data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-xs sm:text-sm px-3 py-1.5"
                                >
                                    Getting Started
                                </TabsTrigger>
                                <TabsTrigger
                                    value="word-study"
                                    className="flex-1 whitespace-nowrap data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-xs sm:text-sm px-3 py-1.5"
                                >
                                    Word Study
                                </TabsTrigger>
                                <TabsTrigger
                                    value="library-topics"
                                    className="flex-1 whitespace-nowrap data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-xs sm:text-sm px-3 py-1.5"
                                >
                                    Library & Topics
                                </TabsTrigger>
                                <TabsTrigger
                                    value="ai-features"
                                    className="flex-1 whitespace-nowrap data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-xs sm:text-sm px-3 py-1.5"
                                >
                                    AI Features
                                </TabsTrigger>
                                <TabsTrigger
                                    value="settings"
                                    className="flex-1 whitespace-nowrap data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-xs sm:text-sm px-3 py-1.5"
                                >
                                    Settings & Data
                                </TabsTrigger>
                            </TabsList>
                        </div>
                    </div>

                    <ScrollArea className="h-[55vh] px-4 sm:px-6 pb-6" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                        {/* Getting Started Tab */}
                        <TabsContent value="getting-started" className="mt-4 space-y-4">
                            <Section icon={BookOpen} title="Navigating the Quran">
                                <p>Use the <strong>Surah selector</strong> in the header to pick any of the 114 surahs. You can search by surah name, Arabic name, or number.</p>
                                <p>Use the <strong>Ayah selector</strong> next to it to jump to a specific verse within the surah.</p>
                                <Step number="1">Select a Surah from the dropdown (searchable)</Step>
                                <Step number="2">Pick an Ayah number to navigate to that verse</Step>
                                <Step number="3">The Arabic text will display prominently with its translation below</Step>
                            </Section>

                            <Section icon={Languages} title="Choosing a Translator" iconColor="#d4af37">
                                <p>Pick from a wide variety of <strong>English translations</strong> using the translator dropdown in the header.</p>
                                <p>You can select <strong>multiple translators at once</strong> to see them side-by-side in the "Translation" tab for deep comparative study.</p>
                                <Tip>Checking multiple boxes in the dropdown will stack the translations in the main view!</Tip>
                            </Section>

                            <Section icon={Keyboard} title="Keyboard Navigation" iconColor="#94a3b8">
                                <p>Use your keyboard for quick navigation:</p>
                                <div className="flex flex-wrap gap-3">
                                    <div className="flex items-center gap-2">
                                        <kbd className="px-2 py-1 bg-[#1e293b] rounded text-xs text-[#f8fafc]">←</kbd>
                                        <span className="text-xs">Previous verse</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <kbd className="px-2 py-1 bg-[#1e293b] rounded text-xs text-[#f8fafc]">→</kbd>
                                        <span className="text-xs">Next verse</span>
                                    </div>
                                </div>
                                <Tip>Arrow keys work when you're not typing in a text field.</Tip>
                            </Section>

                            <Section icon={Copy} title="Copy to Clipboard" iconColor="#94a3b8">
                                <p>Click the <strong>copy icon</strong> on any text block to copy it with the verse reference automatically included (e.g., <code className="text-[#d4af37] text-xs">[2:255] translation text</code>).</p>
                                <p>Available on: Arabic text, standard translation, custom translation, and AI-generated translation.</p>
                            </Section>
                        </TabsContent>

                        {/* Word Study Tab */}
                        <TabsContent value="word-study" className="mt-4 space-y-4">
                            <Section icon={BookOpen} title="Word-by-Word Analysis">
                                <p>Switch to the <strong>"Word by Word"</strong> tab to see each word displayed individually with:</p>
                                <ul className="list-disc list-inside space-y-1 text-xs">
                                    <li><strong>Arabic text</strong> — Uthmani script</li>
                                    <li><strong>Transliteration</strong> — how to pronounce it</li>
                                    <li><strong>English meaning</strong> — standard word translation</li>
                                </ul>
                                <Tip>Click on any word to open the detailed Word Analysis panel!</Tip>
                            </Section>

                            <Section icon={Search} title="Word Analysis Panel" iconColor="#10b981">
                                <p>When you click a word, the <strong>Word Analysis Panel</strong> slides open from the right with two tabs:</p>
                                
                                <p className="font-medium text-[#f8fafc]">📝 Meanings Tab:</p>
                                <ul className="list-disc list-inside space-y-1 text-xs">
                                    <li><strong>Lane's Lexicon</strong> — complete classical definitions, root summaries, and morphological forms</li>
                                    <li><strong>Standard Translation</strong> — the API's default translation for this word</li>
                                    <li><strong>Your Custom Meanings</strong> — meanings you've added with optional reasoning</li>
                                    <li><strong>Root-based meanings</strong> — custom meanings from other words sharing the same Arabic root</li>
                                </ul>

                                <p className="font-medium text-[#f8fafc]">🔍 Verses Tab:</p>
                                <p>Shows <strong>all verses in the Quran</strong> containing words from the same Arabic root. Click any verse to navigate to it and enter <strong>Root Browsing</strong> mode.</p>
                            </Section>

                            <Section icon={Plus} title="Adding Custom Word Meanings" iconColor="#d4af37">
                                <Step number="1">Click a word to open the Analysis Panel</Step>
                                <Step number="2">Click "Add Your Custom Meaning"</Step>
                                <Step number="3">Enter your meaning and optional reasoning</Step>
                                <Step number="4">Click "Save Meaning"</Step>
                                <p className="text-xs text-[#d4af37]">💡 Custom meanings are <strong>shared across all words with the same Arabic root</strong>. Define a root meaning once, and it applies everywhere that root appears!</p>
                            </Section>

                            <Section icon={ArrowRight} title="Root Word Browser" iconColor="#d4af37">
                                <p>When you click a related verse from the Word Analysis panel, a <strong>navigation bar</strong> appears at the bottom of the screen.</p>
                                <p>Use it to browse through all verses containing the same root word:</p>
                                <div className="flex items-center gap-2">
                                    <ChevronLeft className="w-4 h-4 text-[#94a3b8]" />
                                    <span className="text-xs">Previous occurrence</span>
                                    <span className="mx-2 text-[#475569]">|</span>
                                    <ChevronRight className="w-4 h-4 text-[#94a3b8]" />
                                    <span className="text-xs">Next occurrence</span>
                                </div>
                                <p>Click <strong>"Exit"</strong> to close the root browser.</p>
                            </Section>
                        </TabsContent>

                        {/* Library & Topics Tab */}
                        <TabsContent value="library-topics" className="mt-4 space-y-4">
                            <Section icon={Bookmark} title="Saving Verses to Topics" iconColor="#10b981">
                                <p>Click the <strong>Bookmark icon</strong> on any verse to save it to your personal collection.</p>
                                <Step number="1">Click the Bookmark icon near the verse reference</Step>
                                <Step number="2">Select an existing topic or create a new one</Step>
                                <Step number="3">Add an optional comment for why you saved it</Step>
                            </Section>

                            <Section icon={LayoutList} title="Managing Topics" iconColor="#10b981">
                                <p>Click <strong>"Topics"</strong> in the main header to open the Topic Manager.</p>
                                <ul className="list-disc list-inside space-y-1 text-xs">
                                    <li><strong>Reorder:</strong> Use up/down arrows to sequence verses to tell a story</li>
                                    <li><strong>Edit:</strong> Click the pen icon to edit your verse comments</li>
                                    <li><strong>Publish:</strong> Share your topics with the public Library</li>
                                </ul>
                            </Section>

                            <Section icon={Library} title="The Community Library" iconColor="#8b5cf6">
                                <p>In the Topic Manager, switch to the <strong>Library tab</strong> to browse topics published by others.</p>
                                <p>You can <strong>Clone</strong> any public topic to add it to your own collection, tweak verses, and add your own notes.</p>
                            </Section>

                            <Section icon={BookOpen} title="Read as Narrative" iconColor="#d4af37">
                                <p>Once verses are ordered in a topic, click <strong>"Read as Narrative"</strong> to enter the Story Reader.</p>
                                <p>This gives you a seamless, distraction-free reading experience of just the verses in your topic—perfect for studying connected concepts.</p>
                            </Section>
                        </TabsContent>

                        {/* AI Features Tab */}
                        <TabsContent value="ai-features" className="mt-4 space-y-4">
                            <Section icon={Sparkles} title="AI Verse Search" iconColor="#8b5cf6">
                                <p>Click the <strong>AI Search</strong> button (✨) in the header to find verses by description:</p>
                                <Step number="1">Describe the verse in natural language</Step>
                                <Step number="2">Press Ctrl+Enter or click "Find Verses"</Step>
                                <Step number="3">Click any result to navigate there</Step>
                                <p className="text-xs italic">Examples: "The verse about Moses and the burning bush" or "Where Allah talks about honey as medicine"</p>
                            </Section>

                            <Section icon={Sparkles} title="AI Word Meaning Generation" iconColor="#8b5cf6">
                                <p>In the Word Analysis panel, click <strong>"AI: Derive Meaning from Quran"</strong> to have AI analyze all Quranic contexts where the root appears and derive a meaning.</p>
                                <p>The AI considers <strong>every occurrence</strong> of the root across the entire Quran to suggest a contextual meaning with <strong>detailed reasoning</strong>.</p>
                                <Tip>You can save the AI-generated meaning as a custom meaning or discard it.</Tip>
                            </Section>

                            <Section icon={Sparkles} title="AI Translation Generator" iconColor="#d4af37">
                                <p>Switch to the <strong>"AI / Custom"</strong> tab on any verse to generate an AI-powered translation:</p>
                                <Step number="1">Optionally add "Verse Thoughts" — specific notes to guide the AI</Step>
                                <Step number="2">Click "Generate AI Translation"</Step>
                                <Step number="3">Review the translation and AI's reasoning</Step>
                                <Step number="4">Copy it or save it as your custom translation</Step>
                                
                                <p className="text-xs text-[#d4af37]">💡 The AI uses your <strong>custom word meanings</strong> (including root-based ones from other verses), <strong>translation rules</strong>, and <strong>verse thoughts</strong> to generate the translation.</p>
                            </Section>

                            <Section icon={Edit3} title="Custom Translations" iconColor="#10b981">
                                <p>Under the <strong>"AI / Custom"</strong> tab, you can also write, save, edit, and delete your own verse translation:</p>
                                <Step number="1">Click "Add" or "Edit" to provide your translation</Step>
                                <Step number="2">Add optional "Reasoning" to document why you translated it this way</Step>
                                <Step number="3">Click the trash icon to easily delete translations you no longer need</Step>
                                <p>Your saved translations sync to your account or persist locally.</p>
                            </Section>
                        </TabsContent>

                        {/* Settings & Data Tab */}
                        <TabsContent value="settings" className="mt-4 space-y-4">
                            <Section icon={Settings} title="AI Translation Rules" iconColor="#d4af37">
                                <p>Click the <strong>⚙️ gear icon</strong> in the header to open Translation Settings.</p>
                                <p>Here you define <strong>global rules</strong> that guide the AI when generating translations. These rules act as your personal translation methodology.</p>
                                <p>Default rules include principles like:</p>
                                <ul className="list-disc list-inside space-y-1 text-xs">
                                    <li>Authority Rule — Quran as the sole source of truth</li>
                                    <li>Nature Coherence — alignment with natural law</li>
                                    <li>Ethical Priority — choosing elevating interpretations</li>
                                    <li>Custom Root Meaning preference over traditional meanings</li>
                                </ul>
                                <Tip>Customize these rules to match your personal study methodology!</Tip>
                            </Section>

                            <Section icon={Cloud} title="Google Account Cloud Sync" iconColor="#3b82f6">
                                <p>Click the <strong>Google Sign-In icon</strong> in the header to authenticate your account securely.</p>
                                <p>When logged in, all your <strong>custom meanings, translations, rules, and topics</strong> are automatically and securely synced to the cloud, allowing you to access them from any device.</p>
                            </Section>

                            <Section icon={Download} title="Manual Data Export" iconColor="#10b981">
                                <p>Regardless of being logged in, you can click <strong>"Export Data"</strong> in the Settings panel. This downloads a local JSON copy containing your work as a backup.</p>
                            </Section>

                            <Section icon={Upload} title="Manual Data Import" iconColor="#8b5cf6">
                                <p>Click <strong>"Import Data"</strong> to restore from a backup file. Two modes:</p>
                                <ul className="list-disc list-inside space-y-1 text-xs">
                                    <li><strong>Merge</strong> (recommended) — add new data, skip duplicates</li>
                                    <li><strong>Replace All</strong> — delete existing data first, then import</li>
                                </ul>
                            </Section>

                            <Section icon={RotateCcw} title="Reset All Data" iconColor="#ef4444">
                                <p>Click <strong>"Reset All"</strong> to permanently delete all custom meanings, translations, and reset rules to default.</p>
                                <p className="text-xs text-[#ef4444]">⚠️ This cannot be undone! Export your data first as a backup if you're unsure.</p>
                            </Section>
                        </TabsContent>
                        </div>
                    </ScrollArea>
                </Tabs>

                {/* Footer */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-[#1e293b] bg-[#09090b]/50">
                    <p className="text-[10px] sm:text-xs text-[#475569] text-center">
                        Ai Quran — Powered by Gemini AI • Data from Quran.com API & QuranHub
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default HelpGuide;
